import {
	faCheckCircle,
	faEuroSign,
	faList,
	faPen,
	faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef, useState } from "react";
import { Alert, Button, Card, Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ModalAtom from "../../../components/atoms/modal/modal";
import LV_STATUS from "../../../constants/lv-status";
import { useSaveLVMutation } from "../../../store/services/lvService";
import { showToast } from "../../../utils/method";
import AddLVForm from "./add-lv-form";

const AddLVModal = ({
	show,
	onHide,
	projectId,
	onSuccess,
	isLocalProject = false,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [saveLV] = useSaveLVMutation();

	const [loading, setLoading] = useState(false);
	const [showSuccessOptions, setShowSuccessOptions] = useState(false);
	const [createdLv, setCreatedLv] = useState(null);
	const formRef = useRef(null);

	const handleSubmit = async (formData) => {
		try {
			setLoading(true);
			const response = await saveLV({ projectId, formData }).unwrap();
			setCreatedLv(response);
			setShowSuccessOptions(true);
			if (onSuccess) onSuccess(response);
			showToast(t("lv.add-success"), { type: "success" });
		} catch (error) {
			showToast(error || t("errors.general"), { type: "error" });
		} finally {
			setLoading(false);
		}
	};

	const handleModalClose = () => {
		setShowSuccessOptions(false);
		setCreatedLv(null);
		onHide();
	};

	const handleUseBoQEditor = () => {
		handleModalClose();
		navigate(`/projects/${projectId}/lv/${createdLv.id}/edit`);
	};

	const handleAssignCostGroups = () => {
		handleModalClose();
		navigate(`/projects/${projectId}/lv/${createdLv.id}/cost-groups`);
	};

	const renderSuccessOptions = () => {
		const { isNachtraege, statusCode, hasGaebData, name } = createdLv || {};
		const options = [];

		if (isNachtraege) {
			// 1. BoQ Editor - always for Nachtrag
			options.push({
				icon: faPen,
				iconClass: "text-success",
				borderClass: "border-success",
				variant: "success",
				labelKey: "lv.lv-upload-modal.use-boq-editor",
				infoKey: "lv.lv-upload-modal.use-boq-editor-info",
				onClick: handleUseBoQEditor,
			});
			// 2. Upload LV - only if Approved
			if (statusCode === LV_STATUS.APPROVED) {
				options.push({
					icon: faUpload,
					iconClass: "text-warning",
					borderClass: "border-warning",
					variant: "warning",
					labelKey: "lv.lv-upload-modal.upload-lv",
					infoKey: "lv.lv-upload-modal.upload-lv-info",
					// Closes add modal; parent should open upload modal
					onClick: () => { handleModalClose(); onSuccess?.({ ...createdLv, _openUpload: true }); },
				});
			}
		} else if (!hasGaebData) {
			// 3. BoQ Editor - main LV created without a file
			options.push({
				icon: faPen,
				iconClass: "text-success",
				borderClass: "border-success",
				variant: "success",
				labelKey: "lv.lv-upload-modal.use-boq-editor",
				infoKey: "lv.lv-upload-modal.use-boq-editor-info",
				onClick: handleUseBoQEditor,
			});
		} else {
			// 4. Assign Cost Groups - main LV with GAEB file uploaded
			options.push({
				icon: faEuroSign,
				iconClass: "text-primary",
				borderClass: "border-primary",
				variant: "primary",
				labelKey: "lv.lv-upload-modal.assign-cost-groups",
				infoKey: "lv.lv-upload-modal.assign-cost-groups-info",
				onClick: handleAssignCostGroups,
			});
		}

		// 4. View LV List - always
		options.push({
			icon: faList,
			iconClass: "text-secondary",
			borderClass: "border-secondary",
			variant: "outline-secondary",
			labelKey: "lv.lv-upload-modal.view-lv-list",
			infoKey: "lv.lv-upload-modal.view-lv-list-info",
			onClick: handleModalClose,
		});

		const colSize = Math.floor(12 / options.length);

		return (
			<Container>
				<Alert variant="success" className="mb-4">
					<p className="mb-0">
						<FontAwesomeIcon icon={faCheckCircle} className="me-2" />
						<strong>{name}</strong> -{" "}
						{t("lv.lv-upload-modal.lv-upload-success-message")}{" "}
						{t("lv.lv-upload-modal.lv-next-steps")}
					</p>
				</Alert>
				<Row className="g-3">
					{options.map((opt, i) => (
						<Col key={i} md={colSize}>
							<Card className={`h-100 ${opt.borderClass}`}>
								<Card.Body className="text-center">
									<div className="mb-3">
										<FontAwesomeIcon icon={opt.icon} className={`fa-2x ${opt.iconClass}`} />
									</div>
									<Button variant={opt.variant} size="lg" onClick={opt.onClick} className="w-100">
										{t(opt.labelKey)}
									</Button>
									<small className="d-block mt-2">
										<i>{t(opt.infoKey)}</i>
									</small>
								</Card.Body>
							</Card>
						</Col>
					))}
				</Row>
			</Container>
		);
	};

	return (
		<ModalAtom
			title={
				showSuccessOptions
					? t("lv.lv-upload-modal.title-add-lv-success")
					: t("lv.lv-upload-modal.title-add-new-lv")
			}
			body={
				showSuccessOptions
					? renderSuccessOptions()
					: (
						<AddLVForm
							ref={formRef}
							projectId={projectId}
							onSubmit={handleSubmit}
							loading={loading}
							mode="add"
							onCancel={handleModalClose}
							isLocalProject={isLocalProject}
							hideButtons
						/>
					)
			}
			show={show}
			handleClose={handleModalClose}
			handleSubmit={showSuccessOptions ? undefined : () => formRef.current?.triggerSubmit()}
			saveText="common.save"
			disabled={loading}
			size="lg"
			backdrop="static"
			hideCloseButton={showSuccessOptions}
			hideSaveButton={showSuccessOptions}
		/>
	);
};

export default AddLVModal;
